import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Package } from "lucide-react";
import { api } from "@/lib/api";
import { useCurrency } from "@/context/CurrencyContext";
import { getProductPhoto, getVialPhoto, getAccessoryPhoto, getBundlePhoto } from "@/lib/productPhotos";

// Category flyout that opens on hover under a nav pill. Desktop only,
// mobile (touch, no hover) skips the flyout entirely and taps navigate
// straight to the destination. Each flyout renders 2-3 top products
// (image, name, price) plus a "View all →" link that resolves to `to`.
//
// Which products show up per category is driven by a small config below
// (CATEGORY_FILTERS), for the current five nav pills we know exactly
// which backend filter yields the intended list, so we don't have to
// invent a new endpoint.
//
// Usage:
//   <NavPillWithFlyout label="Pen Kits" to="/pen-kits" category="pen" />
const CATEGORY_FILTERS = {
  // format=pen shows only pen-kit SKUs.
  pen: { params: { format: "pen" }, viewAll: "/pen-kits" },
  raw: { params: { format: "raw" }, viewAll: "/raw-vials" },
  bundles: { params: {}, viewAll: "/bundles", useBundles: true },
  best: { params: { best_seller: true }, viewAll: "/peptides?best=1" },
  accessory: { params: { format: "accessory" }, viewAll: "/accessories" },
};

const HOVER_OPEN_DELAY_MS = 100;
const HOVER_CLOSE_DELAY_MS = 200;

export default function NavPillWithFlyout({ label, to, category }) {
  const filter = CATEGORY_FILTERS[category];
  const [open, setOpen] = useState(false);
  const openTimer = useRef(null);
  const closeTimer = useRef(null);
  const isTouchDevice = useRef(false);

  useEffect(() => {
    isTouchDevice.current =
      typeof window !== "undefined" &&
      window.matchMedia("(hover: none)").matches;
  }, []);

  const { data: products = [] } = useQuery({
    queryKey: ["flyout", category],
    queryFn: async () => {
      if (!filter) return [];
      if (filter.useBundles) {
        const { data } = await api.get("/bundles");
        return data;
      }
      const { data } = await api.get("/products", { params: filter.params });
      return data;
    },
    staleTime: 5 * 60 * 1000,
    // Only fetch when the flyout opens for the first time.
    enabled: open,
  });

  const openWithDelay = () => {
    if (isTouchDevice.current) return;
    clearTimeout(closeTimer.current);
    openTimer.current = setTimeout(() => setOpen(true), HOVER_OPEN_DELAY_MS);
  };
  const closeWithDelay = () => {
    clearTimeout(openTimer.current);
    closeTimer.current = setTimeout(() => setOpen(false), HOVER_CLOSE_DELAY_MS);
  };

  useEffect(() => {
    return () => {
      clearTimeout(openTimer.current);
      clearTimeout(closeTimer.current);
    };
  }, []);

  const showItems = (products || []).slice(0, 3);

  return (
    <div
      className="relative"
      onMouseEnter={openWithDelay}
      onMouseLeave={closeWithDelay}
      onFocus={openWithDelay}
      onBlur={closeWithDelay}
      data-testid={`nav-pill-wrap-${category}`}
    >
      <Link
        to={to}
        className="inline-flex items-center whitespace-nowrap px-1 py-1.5 text-ink hover:text-brandblue transition-colors sm:py-0"
        data-testid={`nav-link-${label.toLowerCase().replace(/\s+/g, "-")}`}
      >
        {label}
      </Link>

      {open && filter && (
        <div
          className="pointer-events-auto absolute left-1/2 top-full z-50 hidden -translate-x-1/2 pt-3 md:block"
          data-testid={`nav-flyout-${category}`}
          role="menu"
        >
          <div className="w-[520px] rounded-xl border border-line bg-white shadow-[0_20px_50px_-20px_rgba(15,17,21,0.25)]">
            <div className="grid grid-cols-3 gap-3 p-4">
              {showItems.length > 0
                ? showItems.map((item) => (
                    <FlyoutProductCard
                      key={item.slug || item.id}
                      item={item}
                      useBundle={!!filter.useBundles}
                    />
                  ))
                : [1, 2, 3].map((i) => <FlyoutSkeleton key={i} />)
              }
            </div>
            <Link
              to={filter.viewAll}
              className="flex items-center justify-between border-t border-line px-4 py-3 font-mono text-[11px] font-bold tracking-[0.14em] text-brandblue hover:bg-line/30"
              data-testid={`nav-flyout-viewall-${category}`}
            >
              <span>VIEW ALL {label.toUpperCase()}</span>
              <span aria-hidden="true">→</span>
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}

function FlyoutProductCard({ item, useBundle }) {
  const { format: fmtPrice } = useCurrency();
  const slug = item.slug;
  const name = item.name;
  const price = item.price ?? item.total_price ?? 0;

  // Photo resolution, uses the same shared productPhotos helpers as the grid
  // cards / PDPs so thumbnails match everywhere (white bg, object-contain,
  // centered). Falls back to the Package icon tile only when no photo exists.
  let photo = null;
  if (useBundle) {
    photo = getBundlePhoto(slug);
  } else if (item.format === "raw" || item.format === "vial_kit") {
    photo = getVialPhoto(slug);
  } else if (item.format === "accessory") {
    photo = getAccessoryPhoto(slug);
  } else {
    photo = getProductPhoto(slug, "SEALED") || getProductPhoto(slug, "PEN");
  }

  const to = useBundle ? `/bundle/${slug}` : `/product/${slug}`;

  return (
    <Link
      to={to}
      className="group flex flex-col rounded-lg border border-line bg-white p-2 transition-colors hover:border-brandblue"
      data-testid={`nav-flyout-card-${slug}`}
    >
      <div className="relative flex aspect-square items-center justify-center overflow-hidden rounded-md bg-white">
        {photo ? (
          <img
            src={photo}
            alt={name}
            className="absolute inset-0 h-full w-full object-contain object-center p-2 transition-transform group-hover:scale-105"
            draggable={false}
            data-testid={`nav-flyout-photo-${slug}`}
          />
        ) : (
          // Accessory / missing-photo fallback: a soft, intentional icon
          // tile instead of the generic "content missing" gray dot.
          <div className="flex h-full w-full items-center justify-center bg-[#F5F8FC] text-brandblue/70">
            <Package className="h-8 w-8" strokeWidth={1.5} aria-hidden="true" />
          </div>
        )}
      </div>
      <p className="mt-2 line-clamp-2 font-heading text-[13px] font-bold leading-tight text-ink">
        {name}
      </p>
      <p className="mt-1 font-mono text-[11px] text-subtle">
        {fmtPrice(price)}
      </p>
    </Link>
  );
}

function FlyoutSkeleton() {
  return (
    <div className="flex flex-col rounded-lg border border-line bg-white p-2">
      <div className="aspect-square animate-pulse rounded-md bg-line/40" />
      <div className="mt-2 h-3 w-full animate-pulse rounded bg-line/40" />
      <div className="mt-1 h-2.5 w-2/3 animate-pulse rounded bg-line/40" />
    </div>
  );
}
